import { useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import type { PropertySearchQuery } from 'components/marketplace/helpers/useSearchQueries';

import {
  MarketplaceAvailableSoonCountDocument,
  type MarketplaceSearchAreaInput,
} from '../browse/__generated__/marketplace-browse.zeus';

/** The Zeus search-area input accepts exactly one scope; pick in precedence order. */
export const toSearchAreaInput = (
  location: Pick<PropertySearchQuery, 'market' | 'vertices' | 'zoneIds'>,
): MarketplaceSearchAreaInput | null => {
  if (location.zoneIds?.length) {
    return { zoneIds: location.zoneIds };
  }
  if (location.vertices?.length) {
    return { vertices: location.vertices };
  }
  return location.market ? { market: location.market } : null;
};

type UseAvailableSoonCountOptions = {
  where: MarketplaceSearchAreaInput | null;
  /** Read only when the segment feature is on; the control hides while unresolved. */
  enabled: boolean;
};

/**
 * Available-soon homes in the search area, independent of the filtered result
 * set. Drives whether the inventory segment control renders. Null from the
 * server (failed count read) hides the control.
 */
export const useAvailableSoonCount = ({ where, enabled }: UseAvailableSoonCountOptions) =>
  useQuery({
    queryKey: ['marketplace', 'availableSoonCount', where],
    queryFn: async ({ signal }) => {
      if (!where) {
        throw new Error('availableSoonCount requires a search area');
      }
      const data = await requestZeusFromBrowser(
        MarketplaceAvailableSoonCountDocument,
        { where },
        signal,
      );
      return data.marketplace.availableSoonCount;
    },
    enabled: enabled && where != null,
    staleTime: 60_000,
  });
